import { useEffect, useState } from 'react';
import { MediaType } from 'src/components/Chat/Chat.enum';
import { isTrustedCdnUrl } from 'src/functions/url.functions';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';

/**
 * Hook that validates if a multimedia file is valid using media-aware validation.
 *
 * Uses native browser APIs (Image, Video, Audio elements) to validate media files,
 * which is more accurate and avoids CSP (Content Security Policy) issues compared
 * to fetch-based validation.
 *
 * @param url URL of the multimedia file
 * @param mediaType Type of media to validate (determines validation method)
 * @returns Object with the isValid property that indicates whether the file is valid
 */
export default function useAttachmentValidator(
  url?: string | null,
  mediaType?: MediaType,
) {
  const [isValid, setIsValid] = useState(() => {
    if (url && mediaType && isTrustedCdnUrl(url)) return true;
    if (
      mediaType === MediaType.DOCUMENT ||
      mediaType === MediaType.CONTACT ||
      mediaType === MediaType.LOCATION
    )
      return true;
    return false;
  });

  useEffect(() => {
    if (!url) {
      setIsValid(false);
      return;
    }

    if (!mediaType) {
      LLog.warn(
        {
          component: LOG_COMPONENTS.CHAT,
          method: 'useAttachmentValidator',
          operation: LOG_OPERATIONS.REQUEST,
        },
        'Attachment validation started without mediaType',
        { url: url.substring(0, 100) }, // Truncate URL for privacy
      );
      setIsValid(false);
      return;
    }

    // Trusted CDN URLs skip browser element validation entirely
    if (isTrustedCdnUrl(url)) {
      setIsValid(true);
      return;
    }

    LLog.info(
      {
        component: LOG_COMPONENTS.CHAT,
        method: 'useAttachmentValidator',
        operation: LOG_OPERATIONS.REQUEST,
      },
      'Starting attachment validation',
      { mediaType, url: url.substring(0, 100) },
    );

    let cancelled = false;

    const valid = () => {
      if (!cancelled) {
        setIsValid(true);
        LLog.info(
          {
            component: LOG_COMPONENTS.CHAT,
            method: 'useAttachmentValidator',
            operation: LOG_OPERATIONS.RESPONSE,
          },
          'Attachment validation succeeded',
          { mediaType, url: url.substring(0, 100) },
        );
      }
    };

    const invalid = (error?: Event | string) => {
      if (!cancelled) {
        setIsValid(false);
        const errorMessage =
          error instanceof Event
            ? `Media validation failed for ${mediaType}`
            : error || `Media validation failed for ${mediaType}`;
        LLog.error(
          {
            component: LOG_COMPONENTS.CHAT,
            method: 'useAttachmentValidator',
            operation: LOG_OPERATIONS.ERROR,
          },
          'Attachment validation failed',
          new Error(errorMessage),
          { mediaType, url: url.substring(0, 100) },
        );
      }
    };

    let img: HTMLImageElement | undefined;
    let media: HTMLVideoElement | HTMLAudioElement | undefined;

    switch (mediaType) {
      case MediaType.IMAGE:
      case MediaType.STICKER: {
        img = new Image();
        img.onload = valid;
        img.onerror = invalid;
        img.src = url;
        break;
      }

      case MediaType.VIDEO: {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = valid;
        video.onerror = invalid;
        video.src = url;
        media = video;
        break;
      }

      case MediaType.AUDIO: {
        const audio = document.createElement('audio');
        audio.preload = 'metadata';
        audio.onloadedmetadata = valid;
        audio.onerror = invalid;
        audio.src = url;
        media = audio;
        break;
      }

      case MediaType.DOCUMENT:
        // Cannot reliably validate cross-origin docs
        // Treat as valid and let FileContent handle errors
        setIsValid(true);
        LLog.info(
          {
            component: LOG_COMPONENTS.CHAT,
            method: 'useAttachmentValidator',
            operation: LOG_OPERATIONS.RESPONSE,
          },
          'Document attachment treated as valid (cross-origin validation unreliable)',
          { mediaType, url: url.substring(0, 100) },
        );
        break;

      case MediaType.CONTACT:
      case MediaType.LOCATION:
        setIsValid(true);
        LLog.info(
          {
            component: LOG_COMPONENTS.CHAT,
            method: 'useAttachmentValidator',
            operation: LOG_OPERATIONS.RESPONSE,
          },
          'Contact/Location attachment treated as valid (no URL validation needed)',
          { mediaType, url: url.substring(0, 100) },
        );
        break;

      default:
        setIsValid(false);
        LLog.warn(
          {
            component: LOG_COMPONENTS.CHAT,
            method: 'useAttachmentValidator',
            operation: LOG_OPERATIONS.ERROR,
          },
          'Unknown media type for validation',
          { mediaType, url: url.substring(0, 100) },
        );
    }

    return () => {
      cancelled = true;
      if (img) {
        img.onload = null;
        img.onerror = null;
        img.src = '';
      }
      if (media) {
        media.onloadedmetadata = null;
        media.onerror = null;
        media.removeAttribute('src');
        media.load();
      }
    };
  }, [url, mediaType]);

  return { isValid };
}
